import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Wind, CheckCircle2, ArrowRight, Phone, Mail,
  ShieldCheck, Zap, Droplets, Home, MapPin, ChevronRight
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Contact from '../components/Contact';
import Footer from '../components/Footer';
import './Servicepage.css';

import imgVent from '../assets/ventilation.jpg';

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Installation VMC Ventilation Île-de-France",
  "provider": {
    "@type": "LocalBusiness",
    "name": "SOCLIMATE",
    "telephone": "+33618011895",
    "email": "soclimate.95@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "1 rue de Belloy",
      "addressLocality": "Villaines-sous-Bois",
      "postalCode": "95570",
      "addressCountry": "FR"
    },
    "areaServed": "Île-de-France"
  },
  "description": "Installation et entretien VMC simple flux, double flux et ventilation hygroréglable en Île-de-France.",
  "serviceType": "Installation VMC Ventilation"
};

const PRESTATIONS = [
  { title: 'VMC simple flux', desc: 'Extraction de l\'air vicié dans les pièces humides. Solution économique et efficace.' },
  { title: 'VMC double flux', desc: 'Récupère la chaleur de l\'air extrait pour préchauffer l\'air neuf entrant.' },
  { title: 'Ventilation hygroréglable', desc: 'S\'adapte automatiquement au taux d\'humidité — économies garanties.' },
  { title: "Extraction d'air", desc: 'Systèmes d\'extraction localisée pour cuisines professionnelles et ateliers.' },
  { title: 'Entretien ventilation', desc: 'Nettoyage des bouches, filtres et caissons pour maintenir les performances.' },
  { title: 'Remplacement système', desc: 'Mise en conformité et remplacement de votre ancienne VMC défectueuse.' },
];

const AVANTAGES = [
  { icon: <Zap size={20} strokeWidth={1.8} />, title: 'Qualité de l\'air améliorée', desc: 'Renouvellement constant de l\'air intérieur — moins d\'allergènes et de CO2.' },
  { icon: <Droplets size={20} strokeWidth={1.8} />, title: 'Humidité maîtrisée', desc: 'Élimination de l\'humidité excessive qui cause moisissures et dégradations.' },
  { icon: <ShieldCheck size={20} strokeWidth={1.8} />, title: 'Obligation réglementaire', desc: 'La VMC est obligatoire dans tous les logements depuis 1982 (arrêté du 24/03/1982).' },
  { icon: <Home size={20} strokeWidth={1.8} />, title: 'Confort acoustique', desc: 'Nos systèmes sont sélectionnés pour leur silence et leur discrétion.' },
];

const FAQ = [
  { q: 'La VMC est-elle obligatoire dans un logement ?', a: 'Oui, depuis l\'arrêté du 24 mars 1982, tout logement neuf ou rénové doit disposer d\'un système de ventilation mécanique contrôlée. En l\'absence de VMC, le propriétaire engage sa responsabilité.' },
  { q: 'Quelle différence entre VMC simple flux et double flux ?', a: 'La VMC simple flux extrait l\'air vicié. La VMC double flux extrait l\'air vicié ET insuffle de l\'air frais filtré, en récupérant jusqu\'à 90% de la chaleur — idéale pour les maisons à basse consommation.' },
  { q: 'Combien coûte l\'installation d\'une VMC ?', a: 'Une VMC simple flux coûte entre 500 € et 1 500 € (matériel + pose). Une VMC double flux entre 2 500 € et 5 000 €. Des aides peuvent s\'appliquer.' },
  { q: 'À quelle fréquence faut-il entretenir sa VMC ?', a: 'Un entretien annuel est recommandé : nettoyage des bouches d\'extraction, vérification des filtres et contrôle du caisson. Sans entretien, les performances chutent de 30 à 50% en 3 ans.' },
];

export default function Ventilation() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <Helmet>
        <title>Installation VMC Ventilation Île-de-France | SOCLIMATE</title>
        <meta name="description" content="Installation et entretien VMC simple flux, double flux et ventilation hygroréglable en Île-de-France. Améliorer la qualité de l'air intérieur. Devis gratuit." />
        <meta name="keywords" content="vmc ile de france, installation ventilation, vmc simple flux, vmc double flux, ventilation hygroréglable, entretien vmc 95" />
        <link rel="canonical" href="https://www.soclimate.fr/ventilation" />
        <meta property="og:title" content="Installation VMC Ventilation Île-de-France | SOCLIMATE" />
        <meta property="og:description" content="VMC simple et double flux, ventilation hygroréglable. Devis gratuit en Île-de-France." />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(SCHEMA)}</script>
      </Helmet>

      <Navbar />

      <main className="sp-page">

        <section className="sp-hero sp-hero--teal">
          <div className="sp-hero__bg" style={{ backgroundImage: `url(${imgVent})` }} />
          <div className="sp-hero__veil" />
          <div className="container sp-hero__inner">
            <div className="sp-hero__breadcrumb">
              <Link to="/">Accueil</Link><ChevronRight size={14} />
              <Link to="/solution">Services</Link><ChevronRight size={14} />
              <span>Ventilation</span>
            </div>
            <div className="sp-hero__badge">
              <Wind size={16} strokeWidth={2} />
              VMC Simple Flux · Double Flux · Hygroréglable
            </div>
            <h1 className="sp-hero__h1">
              Installation VMC<br />
              <span>Île-de-France</span>
            </h1>
            <p className="sp-hero__sub">
              Garantissez un air intérieur sain et limitez l'humidité avec nos
              systèmes de ventilation. Installation et entretien dans tout le
              Val-d'Oise et la grande couronne.
            </p>
            <div className="sp-hero__ctas">
              <a href="tel:0618011895" className="sp-cta-primary">
                <Phone size={16} strokeWidth={2} /> 06 18 01 18 95
              </a>
              <a href="#contact" className="sp-cta-ghost">
                Devis gratuit <ArrowRight size={15} strokeWidth={2} />
              </a>
            </div>
            
          </div>
        </section>

        <section className="sp-intro">
          <div className="container sp-intro__grid sp-intro__grid--single">
            <div className="sp-intro__text">
              <h2>Spécialiste VMC en Île-de-France</h2>
              <p>
                La ventilation est un enjeu de santé publique : un air intérieur mal renouvelé
                est 5 à 10 fois plus pollué que l'air extérieur selon l'ADEME. SOCLIMATE installe
                et entretient vos systèmes de ventilation dans le <strong>Val-d'Oise (95)</strong>
                et toute l'Île-de-France.
              </p>
              <p>
                Que vous souhaitiez installer une VMC dans un logement neuf, remplacer
                une ancienne ventilation défaillante ou assurer la maintenance de votre
                système existant, nos techniciens interviennent rapidement.
              </p>
              <ul className="sp-check-list">
                <li><CheckCircle2 size={16} strokeWidth={2} />Diagnostic ventilation gratuit</li>
                <li><CheckCircle2 size={16} strokeWidth={2} />Intervention le jour même possible</li>
                <li><CheckCircle2 size={16} strokeWidth={2} />Toutes marques entretenues</li>
                <li><CheckCircle2 size={16} strokeWidth={2} />Conformité aux normes DTU 68.3</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="sp-section sp-section--gray">
          <div className="container">
            <div className="sp-head">
              <p className="sp-eyebrow"><span />NOS PRESTATIONS</p>
              <h2>Systèmes de ventilation<br /><em>pour tous les logements</em></h2>
            </div>
            <div className="sp-grid-3">
              {PRESTATIONS.map((p, i) => (
                <div key={i} className="sp-presta-card">
                  <div className="sp-presta-card__num">0{i + 1}</div>
                  <h3>{p.title}</h3>
                  <p>{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="sp-section">
          <div className="container">
            <div className="sp-head">
              <p className="sp-eyebrow"><span />POURQUOI LA VMC</p>
              <h2>Les bénéfices d'une <em>ventilation efficace</em></h2>
            </div>
            <div className="sp-grid-2">
              {AVANTAGES.map((a, i) => (
                <div key={i} className="sp-avantage">
                  <div className="sp-avantage__icon">{a.icon}</div>
                  <div><h3>{a.title}</h3><p>{a.desc}</p></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="sp-section sp-section--gray">
          <div className="container">
            <div className="sp-head">
              <p className="sp-eyebrow"><span />ZONES D'INTERVENTION</p>
              <h2>VMC & ventilation<br /><em>dans toute l'Île-de-France</em></h2>
            </div>
            <div className="sp-zones">
              {['Val-d\'Oise (95)', 'Paris (75)', 'Yvelines (78)', 'Essonne (91)', 'Hauts-de-Seine (92)', 'Seine-Saint-Denis (93)', 'Val-de-Marne (94)', 'Seine-et-Marne (77)'].map((z, i) => (
                <div key={i} className="sp-zone-pill"><MapPin size={13} strokeWidth={2} /> {z}</div>
              ))}
            </div>
          </div>
        </section>

        <section className="sp-section">
          <div className="container sp-faq-wrap">
            <div className="sp-head">
              <p className="sp-eyebrow"><span />FAQ</p>
              <h2>Questions fréquentes<br /><em>sur la ventilation VMC</em></h2>
            </div>
            <div className="sp-faq">
              {FAQ.map((item, i) => (
                <details key={i} className="sp-faq__item">
                  <summary className="sp-faq__q">{item.q}</summary>
                  <p className="sp-faq__a">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="sp-cta-band">
          <div className="container sp-cta-band__inner">
            <div>
              <h2>Besoin d'une VMC en Île-de-France ?</h2>
              <p>Installation et entretien — devis gratuit sous 24h.</p>
            </div>
            <div className="sp-cta-band__btns">
              <a href="tel:0618011895" className="sp-cta-primary"><Phone size={16} strokeWidth={2} /> 06 18 01 18 95</a>
              <a href="mailto:soclimate.95@gmail.com" className="sp-cta-ghost sp-cta-ghost--light"><Mail size={16} strokeWidth={2} /> Email</a>
            </div>
          </div>
        </section>

      </main>
      <Contact />
      <Footer />
    </>
  );
}